import { trackEvent } from '@repo/common/analytics';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { useMount } from 'ahooks';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useSuspenseFeatureAccess } from '../../../api';
import { AuthLayoutBase } from '../../../components/Auth/AuthLayouts/AuthLayoutBase';
import { SecodaLiteEducationStep } from './SecodaLiteEducationStep';
import { SecodaLiteIntegrationListStep } from './SecodaLiteIntegrationListStep';
import { SecodaLiteIntegrationSetupStep } from './SecodaLiteIntegrationSetupStep';

export function SecodaLiteOnboardingPage() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [step, setStep] = useState<number>(
		searchParams.get('skip_welcome') ? 1 : 0
	);
	const navigate = useNavigate();
	const { secodaCoreAccess } = useSuspenseFeatureAccess();

	const integration_type = searchParams.get('type');

	// eslint-disable-next-line no-use-effect/no-use-effect
	useEffect(() => {
		trackEvent(`onboarding/${step}`, {
			step: step.toString(),
			secoda_lite: !secodaCoreAccess ? 'true' : 'false',
		});
	}, [step, secodaCoreAccess]);

	useMount(() => {
		// if user reloads the page and they already selected an integration, we should show the integration connection step
		if (integration_type) {
			if (
				integrationList.find(
					(item) =>
						item.type === integration_type &&
						item.secodaEnabled &&
						!item.secodaCoreEnabled
				)
			) {
				setStep(2);
			} else {
				setSearchParams((prev) => {
					prev.delete('type');
					return prev;
				});
			}
		}
	});

	const handleChooseIntegration = useCallback(
		(integrationType: string) => {
			setSearchParams((prev) => {
				prev.set('type', integrationType);
				return prev;
			});
			setStep(2);
		},
		[setSearchParams]
	);

	const handleNext = useCallback(() => {
		if (step === 2) {
			// redirect to viewer onboarding after the user has connected an integration
			navigate('/onboarding/integration');
		} else {
			setStep((prev) => prev + 1);
		}
	}, [step, navigate]);

	const handleBack = useCallback(() => {
		setStep((prev) => (prev > 0 ? prev - 1 : 0));
	}, []);

	return (
		<AuthLayoutBase>
			{
				[
					<SecodaLiteEducationStep key="education" onNext={handleNext} />,
					<SecodaLiteIntegrationListStep
						key="integration-list"
						onBack={handleBack}
						onChooseIntegration={handleChooseIntegration}
					/>,
					<SecodaLiteIntegrationSetupStep
						key={`integration-connection-${integration_type}`}
						onBack={handleBack}
						onNext={handleNext}
					/>,
				][step]
			}
		</AuthLayoutBase>
	);
}
